import React, { useEffect, useState, useCallback } from 'react';
import { Container, CircularProgress, Stack, Box, Typography, Button } from '@mui/material';
import { useDispatch } from 'react-redux';
import { NotificationSettingsApi } from '../api/NotificationSettingsApi';
import { setSuccessNotification, setErrorNotification } from '../actions/notificationAction';
import useTranslation from '../hooks/useTranslation';
import { NotificationSettings } from '../shared-components';

const INITIAL_FORM = { notify_low_stock: true, notify_tracking: true, notify_waitlist: true };

const NotificationSettingsContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const showError = (e) => {
    dispatch(setErrorNotification({ message: e?.response?.data?.message || e.message }));
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await NotificationSettingsApi.getNotificationSettings();
      setForm({
        notify_low_stock: !!data?.notify_low_stock,
        notify_tracking: !!data?.notify_tracking,
        notify_waitlist: !!data?.notify_waitlist,
      });
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (key, checked) => {
    setForm((prev) => ({ ...prev, [key]: checked }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await NotificationSettingsApi.updateNotificationSettings(form);
      dispatch(setSuccessNotification({ message: t('response_messages.updated') || 'Updated' }));
    } catch (e) {
      showError(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Container maxWidth="xl">
      <Stack spacing={3} sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('labels.notification_settings')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('labels.notification_page_desc')}</Typography>
          </Box>
          <Button variant="contained" onClick={saveSettings} disabled={saving}>
            {saving ? t('labels.saving') : t('labels.save')}
          </Button>
        </Stack>
        <NotificationSettings
          value={form}
          onChange={handleChange}
          onSave={saveSettings}
          saving={saving}
          description={t('labels.notification_settings_desc')}
          showActions={false}
        />
      </Stack>
    </Container>
  );
};

export default NotificationSettingsContainer;